import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { Dispatch } from "redux"
import { UseQueryOptions } from "@tanstack/react-query"
import { OK, ResourceResult } from "helpers/data-statuses"
import { SelectedBasket, SelectedBenchmark, SelectedEtf, SelectedExperiment, SelectedIndex, SelectedInlineInstrument, SelectedInstrument, SelectedPortfolio, SelectedRulesInstrument } from "helpers/types/instruments/selected"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { useNullableQuery } from "hooks/queries"
import { IsoDate } from "helpers/types/dates"
import { Creators } from "model/apiWrapper"
import { useAccountAlias, useIsOpPro } from "./account"
import { getPortfolioQueryDefinition, PortfolioMetadata } from "./position-sets/portfolios"
import { ExperimentMetadata, getExperimentQueryDefinition } from "./position-sets/experiments"
import { BasketMetadata, getBasketQueryDefinition } from "./position-sets/baskets"
import { EtfMetadata, getSecurityQueryDefinition } from "./position-sets/securities"
import { getIndexQueryDefinition, IndexMetadata } from "./position-sets/indexes"
import { BenchmarkMetadata, getBenchmarkQueryDefinition } from "./position-sets/benchmarks"
import { getInlineQueryDefinition, InlineMetadata } from "./position-sets/inline"
import { RulesMetadata } from "./position-sets/rules"

// TODO [APP-2592] - remove queryKeyPrefix (and its use in getPositionSetQuery) once corrected
function getPositionSetQuery<SelectedT extends SelectedInstrument>(
  modelId: string,
  positionSet: SelectedT | undefined,
  metadataDate: IsoDate | undefined,
  dispatch: Dispatch,
  accountAlias: string,
  isOpPro = false,
  queryKeyPrefix?: string,
): UseQueryOptions<unknown, Error, ToMetadata<SelectedT>> | null {
  if (!positionSet || !metadataDate) {
    return null
  }

  let queryDefinition
  switch (positionSet.type) {
    case "PORTFOLIO":
      if (positionSet.experimentId === undefined) {
        queryDefinition = getPortfolioQueryDefinition(positionSet, modelId, metadataDate, isOpPro)
      } else {
        queryDefinition = getExperimentQueryDefinition(positionSet, modelId, metadataDate)
      }
      break
    case "BASKET":
    case "SWAP":
      queryDefinition = getBasketQueryDefinition(positionSet, modelId, metadataDate)
      break
    case "ETF":
      queryDefinition = getSecurityQueryDefinition(positionSet, modelId, metadataDate)
      break
    case "INDEX":
      queryDefinition = getIndexQueryDefinition(positionSet, modelId, metadataDate)
      break
    case "BENCHMARK":
      queryDefinition = getBenchmarkQueryDefinition(positionSet, modelId)
      break
    case "RULES": {
      const result = getPositionSetQuery(modelId, positionSet.rulesConfiguration.source.positionSet, metadataDate, dispatch, accountAlias, isOpPro, queryKeyPrefix)

      if (!result) {
        return null
      }

      const weightingRule = positionSet.rulesConfiguration.rules.find(rule => rule.weighting)?.weighting
      let name = "Rule"
      if (weightingRule?.equalWeighting) {
        name = "Equal Weight"
      } else if (weightingRule?.idioWeighting) {
        name = "Equal Idio Weight"
      }
      return {
        ...result,
        select: data => ({
          ...result.select?.(data),
          name,
          type: positionSet.type,
        }) as ToMetadata<SelectedT>,
      }
    }
    case undefined:
      positionSet.type satisfies undefined
      queryDefinition = getInlineQueryDefinition(positionSet, modelId, metadataDate)
      break
  }

  if (!queryDefinition) {
    return null
  }

  return {
    queryKey: [...(queryKeyPrefix ? [queryKeyPrefix] : []), ...queryDefinition.queryKey],
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(accountAlias, queryDefinition.query, queryDefinition.variables, resolve, reject))
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select: queryDefinition.select as any,
    enabled: true,
  }
}

// Helps to ensure subset of outputs matches subset of inputs
// So a selected Benchmark OR Portfolio can only return Benchmark OR Portfolio metadata
type ToMetadata<T extends SelectedInstrument> =
  T extends SelectedPortfolio ? PortfolioMetadata
  : T extends SelectedExperiment ? ExperimentMetadata
  : T extends SelectedEtf ? EtfMetadata
  : T extends SelectedBasket ? BasketMetadata
  : T extends SelectedBenchmark ? BenchmarkMetadata
  : T extends SelectedIndex ? IndexMetadata
  : T extends SelectedInlineInstrument ? InlineMetadata
  : T extends SelectedRulesInstrument ? RulesMetadata
  : InstrumentMetadata

// TODO [APP-2592] - remove the 4th parameter (and its use in getPositionSetQuery) once corrected
export function usePositionSetMetadata<SelectedT extends SelectedInstrument = SelectedInstrument>(modelId: string, positionSet: SelectedT | undefined, metadataDate: IsoDate | undefined, queryKeyPrefix?: string): ResourceResult<ToMetadata<SelectedT> | null> {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const isOpPro = useIsOpPro()

  const query = useMemo(
    () => getPositionSetQuery<SelectedT>(modelId, positionSet, metadataDate, dispatch, accountAlias, isOpPro, queryKeyPrefix),
    [dispatch, accountAlias, modelId, positionSet, metadataDate, isOpPro, queryKeyPrefix],
  )

  const result = useNullableQuery(query)

  if (!query) {
    return [null, OK]
  }

  return result
}
